import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import {
  frameThickness,
  glassHingeWidth,
  glassHingeHeight,
  glassLockWidth,
  glassLockHeight,
} from '@/shared/config/model-constants'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

const allDoorPanelIds = [
  'left_door_glass_panel_1',
  'left_door_glass_panel_2',
  'right_door_glass_panel_1',
  'right_door_glass_panel_2',
] as const

const bottomFrameIds = [
  'bottom_left_frame_profile',
  'bottom_left_door_frame_profile',
  'bottom_center_frame_profile',
  'bottom_right_door_frame_profile',
  'bottom_right_frame_profile',
] as const

/** Template7 Layout: [LeftFrame][LeftGlass][LeftDoorSection][Center][RightDoorSection][RightGlass][RightFrame] */
function recalculatePositions(model: ModelData) {
  const params = model.parameters!
  const leftFrame = getParam(params, 'left_frame_profile')
  const topFrame = getParam(params, 'top_frame_profile')
  if (!leftFrame || !topFrame) return

  let currentX = leftFrame.width
  const topY = topFrame.height

  const leftGlass = getParam(params, 'left_glass_panel')
  const leftDoorTop = getParam(params, 'left_door_top_glass_panel')
  const leftDoor1 = getParam(params, 'left_door_glass_panel_1')
  const leftDoor2 = getParam(params, 'left_door_glass_panel_2')
  const centerGlass = getParam(params, 'center_glass_panel')
  const rightDoor1 = getParam(params, 'right_door_glass_panel_1')
  const rightDoor2 = getParam(params, 'right_door_glass_panel_2')
  const rightDoorTop = getParam(params, 'right_door_top_glass_panel')
  const rightGlass = getParam(params, 'right_glass_panel')
  const rightFrame = getParam(params, 'right_frame_profile')

  if (leftGlass) {
    updateParam(model, 'left_glass_panel', { position: { x: currentX, y: topY, z: 0 } })
    currentX += leftGlass.width
  }
  if (leftDoorTop) {
    updateParam(model, 'left_door_top_glass_panel', { position: { x: currentX, y: topY, z: 0 } })
  }
  if (leftDoor1 && leftDoorTop) {
    updateParam(model, 'left_door_glass_panel_1', { position: { x: currentX, y: topY + leftDoorTop.height, z: 0 } })
    currentX += leftDoor1.width
  }
  if (leftDoor2 && leftDoorTop) {
    updateParam(model, 'left_door_glass_panel_2', { position: { x: currentX, y: topY + leftDoorTop.height, z: 0 } })
    currentX += leftDoor2.width
  }
  if (centerGlass) {
    updateParam(model, 'center_glass_panel', { position: { x: currentX, y: topY, z: 0 } })
    currentX += centerGlass.width
  }
  if (rightDoorTop) {
    updateParam(model, 'right_door_top_glass_panel', { position: { x: currentX, y: topY, z: 0 } })
  }
  if (rightDoor1 && rightDoorTop) {
    updateParam(model, 'right_door_glass_panel_1', { position: { x: currentX, y: topY + rightDoorTop.height, z: 0 } })
    currentX += rightDoor1.width
  }
  if (rightDoor2 && rightDoorTop) {
    updateParam(model, 'right_door_glass_panel_2', { position: { x: currentX, y: topY + rightDoorTop.height, z: 0 } })
    currentX += rightDoor2.width
  }
  if (rightGlass) {
    updateParam(model, 'right_glass_panel', { position: { x: currentX, y: topY, z: 0 } })
  }
  if (rightFrame) {
    updateParam(model, 'right_frame_profile', { position: { x: (model.width as number) - rightFrame.width, y: 0, z: 0 } })
  }

  // Update bottom frame positions
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  if (bottomLeft && leftGlass) {
    updateParam(model, 'bottom_left_frame_profile', { position: { x: leftFrame.width, y: (model.height as number) - bottomLeft.height, z: 0 } })
  }
  const bottomLeftDoor = getParam(model.parameters!, 'bottom_left_door_frame_profile')
  if (bottomLeftDoor && leftDoor1) {
    updateParam(model, 'bottom_left_door_frame_profile', { position: { x: leftDoor1.position!.x, y: (model.height as number) - bottomLeftDoor.height, z: 0 } })
  }
  const bottomCenter = getParam(model.parameters!, 'bottom_center_frame_profile')
  if (bottomCenter && centerGlass) {
    const updatedCenter = getParam(model.parameters!, 'center_glass_panel')
    if (updatedCenter) {
      updateParam(model, 'bottom_center_frame_profile', { position: { x: updatedCenter.position!.x, y: (model.height as number) - bottomCenter.height, z: 0 } })
    }
  }
  const bottomRightDoor = getParam(model.parameters!, 'bottom_right_door_frame_profile')
  if (bottomRightDoor && rightDoor1) {
    updateParam(model, 'bottom_right_door_frame_profile', { position: { x: rightDoor1.position!.x, y: (model.height as number) - bottomRightDoor.height, z: 0 } })
  }
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  if (bottomRight && rightGlass) {
    updateParam(model, 'bottom_right_frame_profile', { position: { x: rightGlass.position!.x, y: (model.height as number) - bottomRight.height, z: 0 } })
  }
}

function updateGlassPanelHardwarePositionsX(model: ModelData, panelWidth: number, panelId: string) {
  const panel = getParam(model.parameters!, panelId)
  if (!panel?.hardware) return

  const updatedHardware = { ...panel.hardware }
  const isLeftDoor1 = panelId === 'left_door_glass_panel_1'
  const isRightDoor1 = panelId === 'right_door_glass_panel_1'
  const isLeftDoor2 = panelId === 'left_door_glass_panel_2'
  const isRightDoor2 = panelId === 'right_door_glass_panel_2'

  if (updatedHardware.locks) {
    updatedHardware.locks = updatedHardware.locks.map((lock) => {
      if (isLeftDoor1 || isRightDoor1) {
        return { ...lock, position: { ...lock.position, x: panelWidth - glassLockWidth * 1.5 } }
      }
      const ratio = panel.width > 0 ? lock.position.x / panel.width : 0.9
      return { ...lock, position: { ...lock.position, x: panelWidth * ratio } }
    })
  }
  if (updatedHardware.handles) {
    updatedHardware.handles = updatedHardware.handles.map((handle) => {
      const ratio = panel.width > 0 ? handle.position.x / panel.width : 0.5
      return { ...handle, position: { ...handle.position, x: panelWidth * ratio } }
    })
  }
  if ((isLeftDoor2 || isRightDoor2) && updatedHardware.hinges) {
    updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
      if (hinge.positionId === 'door_2_right_top_hinge_1') {
        return { ...hinge, position: { ...hinge.position, x: panelWidth - glassHingeWidth / 2 } }
      }
      if (hinge.positionId === 'door_2_right_bottom_hinge_2') {
        return { ...hinge, position: { ...hinge.position, x: panelWidth - glassHingeWidth } }
      }
      return hinge
    })
  }
  updateParam(model, panelId, { hardware: updatedHardware })
}

function updateDoorHardwareForHeight(model: ModelData, doorHeight: number, panelId: string) {
  const panel = getParam(model.parameters!, panelId)
  if (!panel?.hardware) return

  const updatedHardware = { ...panel.hardware }
  if (updatedHardware.handles) {
    updatedHardware.handles = updatedHardware.handles.map((h) => ({ ...h, position: { ...h.position, y: doorHeight * 0.55 } }))
  }
  if (updatedHardware.hinges) {
    updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
      if (hinge.position.y < 0) return hinge
      return { ...hinge, position: { ...hinge.position, y: doorHeight - glassHingeHeight } }
    })
  }
  if (updatedHardware.locks) {
    updatedHardware.locks = updatedHardware.locks.map((lock) => ({ ...lock, position: { ...lock.position, y: doorHeight - glassLockHeight } }))
  }
  updateParam(model, panelId, { hardware: updatedHardware })
}

const totalWidth: DimensionHandler = (model, _initial, numValue) => {
  const widthDiff = numValue - (model.width as number)
  model.width = numValue
  updateParam(model, 'top_frame_profile', { width: numValue })

  if (widthDiff !== 0) {
    const share = widthDiff / 3
    const leftFrame = getParam(model.parameters!, 'left_frame_profile')
    const ft = leftFrame?.width || frameThickness

    for (const [bottomId, glassId] of [['bottom_left_frame_profile', 'left_glass_panel'], ['bottom_right_frame_profile', 'right_glass_panel']] as const) {
      const bf = getParam(model.parameters!, bottomId)
      if (bf) {
        const newW = Math.max(100, bf.width + share)
        updateParam(model, bottomId, { width: newW })
        if (getParam(model.parameters!, glassId)) {
          updateParam(model, glassId, { width: Math.max(100, newW - ft) })
        }
      }
    }

    const bc = getParam(model.parameters!, 'bottom_center_frame_profile')
    if (bc) {
      const newW = Math.max(100, bc.width + share)
      updateParam(model, 'bottom_center_frame_profile', { width: newW })
      if (getParam(model.parameters!, 'center_glass_panel')) {
        updateParam(model, 'center_glass_panel', { width: newW })
      }
    }

    recalculatePositions(model)
  }
  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue) => {
  model.height = numValue
  updateParam(model, 'left_frame_profile', { height: numValue })
  updateParam(model, 'right_frame_profile', { height: numValue })

  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const leftDoorTop = getParam(model.parameters!, 'left_door_top_glass_panel')
  const rightDoorTop = getParam(model.parameters!, 'right_door_top_glass_panel')

  if (topFrame) {
    const availableHeight = numValue - topFrame.height * 2
    ;['left_glass_panel', 'center_glass_panel', 'right_glass_panel'].forEach((id) => {
      if (getParam(model.parameters!, id)) updateParam(model, id, { height: availableHeight })
    })

    if (leftDoorTop && rightDoorTop) {
      const doorHeight = numValue - topFrame.height - leftDoorTop.height
      const doorY = topFrame.height + leftDoorTop.height
      allDoorPanelIds.forEach((id) => {
        const p = getParam(model.parameters!, id)
        if (p) {
          updateParam(model, id, { height: doorHeight, position: { ...p.position, y: doorY } })
          updateDoorHardwareForHeight(model, doorHeight, id)
        }
      })
    }

    const bottomY = numValue - topFrame.height
    bottomFrameIds.forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f) updateParam(model, id, { position: { ...f.position, y: bottomY } })
    })

    recalculatePositions(model)
  }
  return model
}

const transomHeight: DimensionHandler = (model, _initial, numValue) => {
  const leftDoorTop = getParam(model.parameters!, 'left_door_top_glass_panel')
  const rightDoorTop = getParam(model.parameters!, 'right_door_top_glass_panel')
  const leftDoor1 = getParam(model.parameters!, 'left_door_glass_panel_1')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  if (!leftDoorTop || !rightDoorTop || !leftDoor1 || !topFrame) return model

  const newPanelHeight = numValue - topFrame.height
  updateParam(model, 'left_door_top_glass_panel', { height: newPanelHeight, position: { ...leftDoorTop.position, y: frameThickness } })
  updateParam(model, 'right_door_top_glass_panel', { height: newPanelHeight, position: { ...rightDoorTop.position, y: frameThickness } })

  allDoorPanelIds.forEach((id) => {
    const p = getParam(model.parameters!, id)
    if (p) updateParam(model, id, { position: { ...p.position, y: numValue } })
  })

  const newTotalHeight = numValue + leftDoor1.height
  model.height = newTotalHeight
  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })

  ;['left_glass_panel', 'center_glass_panel', 'right_glass_panel'].forEach((id) => {
    if (getParam(model.parameters!, id)) updateParam(model, id, { height: newTotalHeight - frameThickness - frameThickness })
  })

  bottomFrameIds.forEach((id) => {
    const f = getParam(model.parameters!, id)
    if (f) updateParam(model, id, { position: { ...f.position, y: newTotalHeight - frameThickness } })
  })

  return model
}

const doorPanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const leftDoorTop = getParam(model.parameters!, 'left_door_top_glass_panel')
  if (!leftDoorTop) return model

  allDoorPanelIds.forEach((id) => {
    if (getParam(model.parameters!, id)) {
      updateParam(model, id, { height: numValue })
      updateDoorHardwareForHeight(model, numValue, id)
    }
  })

  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  if (topFrame) {
    const newTotalHeight = topFrame.height + leftDoorTop.height + numValue
    model.height = newTotalHeight
    updateParam(model, 'left_frame_profile', { height: newTotalHeight })
    updateParam(model, 'right_frame_profile', { height: newTotalHeight })

    ;['left_glass_panel', 'center_glass_panel', 'right_glass_panel'].forEach((id) => {
      if (getParam(model.parameters!, id)) updateParam(model, id, { height: newTotalHeight - frameThickness - frameThickness })
    })

    bottomFrameIds.forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f) updateParam(model, id, { position: { ...f.position, y: newTotalHeight - frameThickness } })
    })
  }

  recalculatePositions(model)
  return model
}

const leftFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  if (!bottomLeft || !leftFrame) return model

  const diff = numValue - bottomLeft.width
  updateParam(model, 'bottom_left_frame_profile', { width: numValue })
  updateParam(model, 'left_glass_panel', { width: numValue - leftFrame.width })
  model.width = (model.width as number) + diff
  updateParam(model, 'top_frame_profile', { width: model.width as number })
  recalculatePositions(model)
  return model
}

const centerFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const center = getParam(model.parameters!, 'center_glass_panel')
  if (!center) return model

  const diff = numValue - center.width
  updateParam(model, 'center_glass_panel', { width: numValue })
  updateParam(model, 'bottom_center_frame_profile', { width: numValue })
  model.width = (model.width as number) + diff
  updateParam(model, 'top_frame_profile', { width: model.width as number })
  recalculatePositions(model)
  return model
}

const rightFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  if (!bottomRight || !rightFrame) return model

  const diff = numValue - bottomRight.width
  updateParam(model, 'bottom_right_frame_profile', { width: numValue })
  updateParam(model, 'right_glass_panel', { width: numValue - rightFrame.width })
  model.width = (model.width as number) + diff
  updateParam(model, 'top_frame_profile', { width: model.width as number })
  recalculatePositions(model)
  return model
}

const leftDoorSectionWidth: DimensionHandler = (model, _initial, numValue) => {
  const ld1 = getParam(model.parameters!, 'left_door_glass_panel_1')
  const ld2 = getParam(model.parameters!, 'left_door_glass_panel_2')
  if (!ld1 || !ld2) return model

  const diff = numValue - (ld1.width + ld2.width)
  const half = diff / 2
  const newLd1 = Math.max(200, ld1.width + half)
  const newLd2 = Math.max(200, ld2.width + half)

  updateParam(model, 'left_door_glass_panel_1', { width: newLd1 })
  updateParam(model, 'left_door_glass_panel_2', { width: newLd2 })
  updateParam(model, 'left_door_top_glass_panel', { width: numValue })
  updateGlassPanelHardwarePositionsX(model, newLd1, 'left_door_glass_panel_1')
  updateGlassPanelHardwarePositionsX(model, newLd2, 'left_door_glass_panel_2')

  model.width = (model.width as number) + diff
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  const rd1 = getParam(model.parameters!, 'right_door_glass_panel_1')
  const rd2 = getParam(model.parameters!, 'right_door_glass_panel_2')
  model.doorConfiguration = {
    ...model.doorConfiguration!,
    totalDoorWidth: numValue + (rd1?.width || 0) + (rd2?.width || 0),
    individualDoorWidth: [newLd1, newLd2, rd1?.width || 0, rd2?.width || 0],
  }

  recalculatePositions(model)
  return model
}

const rightDoorSectionWidth: DimensionHandler = (model, _initial, numValue) => {
  const rd1 = getParam(model.parameters!, 'right_door_glass_panel_1')
  const rd2 = getParam(model.parameters!, 'right_door_glass_panel_2')
  if (!rd1 || !rd2) return model

  const diff = numValue - (rd1.width + rd2.width)
  const half = diff / 2
  const newRd1 = Math.max(200, rd1.width + half)
  const newRd2 = Math.max(200, rd2.width + half)

  updateParam(model, 'right_door_glass_panel_1', { width: newRd1 })
  updateParam(model, 'right_door_glass_panel_2', { width: newRd2 })
  updateParam(model, 'right_door_top_glass_panel', { width: numValue })
  updateParam(model, 'bottom_right_door_frame_profile', { width: numValue })
  updateGlassPanelHardwarePositionsX(model, newRd1, 'right_door_glass_panel_1')
  updateGlassPanelHardwarePositionsX(model, newRd2, 'right_door_glass_panel_2')

  model.width = (model.width as number) + diff
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  const ld1 = getParam(model.parameters!, 'left_door_glass_panel_1')
  const ld2 = getParam(model.parameters!, 'left_door_glass_panel_2')
  model.doorConfiguration = {
    ...model.doorConfiguration!,
    totalDoorWidth: (ld1?.width || 0) + (ld2?.width || 0) + numValue,
    individualDoorWidth: [ld1?.width || 0, ld2?.width || 0, newRd1, newRd2],
  }

  recalculatePositions(model)
  return model
}

export const model7DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  transomHeight,
  doorPanelHeight,
  leftFrameWidth,
  centerFrameWidth,
  rightFrameWidth,
  leftDoorSectionWidth,
  rightDoorSectionWidth,
}
